import { useEffect, useRef } from "react";

const DOT_RGB = "52,211,153";

export default function HeroBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let raf = 0;
    let pts = [];
    let W = 0;
    let H = 0;

    const size = () => {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
      const count = Math.min(70, Math.floor(W / 22));
      pts = Array.from({ length: count }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.14,
        vy: (Math.random() - 0.5) * 0.14,
        r: Math.random() * 1.3 + 0.4,
      }));
      // Resizing clears the canvas, so repaint if the loop is parked.
      if (!raf) draw();
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      for (const p of pts) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${DOT_RGB},0.5)`;
        ctx.fill();
      }
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const d = dx * dx + dy * dy;
          if (d < 9000) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(${DOT_RGB},${0.1 * (1 - d / 9000)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
    };

    const loop = () => {
      draw();
      raf = requestAnimationFrame(loop);
    };

    const stop = () => {
      cancelAnimationFrame(raf);
      raf = 0;
    };

    size();
    window.addEventListener("resize", size);

    /* Decorative only, and the link pass is O(points²) per frame — so it runs
       while the hero is on screen and is parked the rest of the session. */
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        stop();
      } else if (reduce) {
        draw(); // one static frame only
      } else if (!raf) {
        raf = requestAnimationFrame(loop);
      }
    });
    observer.observe(canvas);

    return () => {
      stop();
      observer.disconnect();
      window.removeEventListener("resize", size);
    };
  }, []);

  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      {/* faint grid, faded out toward the edges */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(color-mix(in srgb, var(--color-primary) 6%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in srgb, var(--color-primary) 6%, transparent) 1px, transparent 1px)",
          backgroundSize: "52px 52px",
          maskImage:
            "radial-gradient(ellipse 90% 70% at 60% 30%, #000 40%, transparent 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 90% 70% at 60% 30%, #000 40%, transparent 85%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-90"
        style={{
          background:
            "radial-gradient(70% 75% at 82% 28%, color-mix(in srgb, var(--color-primary) 18%, transparent), transparent 72%), radial-gradient(80% 70% at 50% 100%, color-mix(in srgb, var(--color-primary) 11%, transparent), transparent 75%)",
        }}
      />

      {/* constellation */}
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
    </div>
  );
}
